/**
 * What each SPA route claims to be, for both the crawler and the reader.
 *
 * The app is a single-page bundle, so nginx answers every route with the same
 * `index.html`. That file used to declare one title ("ChooseChow"), a
 * description written about the platform runtime rather than about food, and no
 * canonical at all — which meant every route on the site presented itself to a
 * search engine as the same untitled page, and every link pasted into a group
 * chat rendered as a bare URL.
 *
 * A canonical is a claim about identity and it has to be true in the bytes the
 * crawler receives, not repaired afterwards by script: when the served HTML and
 * the hydrated DOM disagree, a crawler resolves the conflict by picking its own
 * canonical. So this table is read twice from one place — the Vite build writes
 * a real per-route HTML file from it, and the running app applies the same
 * values on client navigation. They agree because there is only one table.
 *
 * Entity pages (/kitchen/…, /dish/…, /kitchens) are deliberately absent: those
 * are server-rendered from the database by internal/service/seo and never reach
 * this bundle.
 */

export interface RouteMeta {
  /** Path without a trailing slash. "/" is the site root. */
  path: string
  title: string
  description: string
  /**
   * Whether this page belongs in a search index.
   *
   * Sign-in, the cart and every account surface are excluded. They are
   * near-identical to each other, carry nothing a searcher wants, and are
   * exactly the thin pages that get reported as duplicates — a crawler
   * comparing /login and /register sees one form and one sentence.
   */
  indexable: boolean
}

/**
 * The absolute origin this build is served from.
 *
 * `VITE_SITE_ORIGIN` must match the server's `SITE_ORIGIN`, because the two
 * halves of the site emit canonical URLs independently and a mismatch means the
 * app and the kitchen pages claim to be different websites. The fallback is the
 * production origin rather than a placeholder: a canonical is either right or
 * it is actively harmful, and "http://localhost:5173" in a crawled page is the
 * harmful case.
 */
/**
 * Read `VITE_SITE_ORIGIN` from whichever environment is evaluating this module.
 *
 * This file is imported twice: by the app bundle, where Vite substitutes
 * `import.meta.env`, and by `vite.config.ts`, which runs in Node under a
 * tsconfig that has no `import.meta.env` at all. Both accesses are guarded so
 * neither context needs the other's types.
 */
function configuredOrigin(): string {
  const viteEnv = (import.meta as unknown as { env?: Record<string, string | undefined> }).env
  const nodeEnv = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process
    ?.env
  return (viteEnv?.VITE_SITE_ORIGIN || nodeEnv?.VITE_SITE_ORIGIN || '').trim()
}

export const SITE_ORIGIN = (configuredOrigin() || 'https://choosechow.com').replace(/\/+$/, '')

/** The one-line pitch, reused wherever a page has nothing more specific. */
export const SITE_TAGLINE = 'Homemade food from kitchens near you'

export const ROUTE_META: readonly RouteMeta[] = [
  { path: '/notifications', title: 'Sounds & notifications · ChooseChow', description: 'Control your ChooseChow sounds and device notifications.', indexable: false },
  {
    path: '/',
    // Written for a hungry person, not for the platform. The old title was the
    // bare word "ChooseChow", which tells a searcher nothing, and the old
    // description named the runtime the site happens to be built on.
    title: 'ChooseChow — Order homemade Nigerian food from kitchens near you',
    description:
      'Order jollof, egusi, suya and more from verified home chefs, food trucks and market vendors cooking near you. Made fresh when you order, delivered hot across Abuja and Lagos.',
    indexable: true,
  },
  {
    path: '/privacy',
    title: 'Privacy Policy — ChooseChow',
    description:
      'How ChooseChow collects, uses and protects your personal data, including delivery addresses and payment details, under the Nigeria Data Protection Act 2023.',
    indexable: true,
  },
  {
    path: '/terms',
    title: 'Terms of Service — ChooseChow',
    description:
      'The terms you agree to when ordering food on ChooseChow or selling as a home chef, food truck or market vendor.',
    indexable: true,
  },
  {
    // Indexable on purpose. Google Play requires the deletion URL to be
    // publicly reachable, and a person looking for it after uninstalling the
    // app will look for it in a search engine, not in a footer.
    path: '/delete-account',
    title: 'Delete your account and data — ChooseChow',
    description:
      'How to request deletion of your ChooseChow account or personal data, what we remove, and what we are required to keep.',
    indexable: true,
  },
  {
    path: '/plans',
    title: 'Meal planning — ChooseChow',
    description: 'Plan meals for yourself or your household. Planning is available to everyone. Pay for meals and service fees at checkout.',
    indexable: true,
  },
  {
    path: '/guidelines',
    title: 'Kitchen Guidelines & Hygiene Standards — ChooseChow',
    description:
      'Kitchen standards, verification requirements, and hygiene rules for home chefs and food makers on ChooseChow.',
    indexable: true,
  },
  {
    path: '/kitchen-guidelines',
    title: 'Kitchen Guidelines & Hygiene Standards — ChooseChow',
    description:
      'Kitchen standards, verification requirements, and hygiene rules for home chefs and food makers on ChooseChow.',
    indexable: true,
  },

  // Everything below is a working surface, not a landing page. Each declares a
  // canonical of its own so nothing points at the homepage, and each is kept
  // out of the index rather than left to compete with it.
  {
    path: '/login',
    title: 'Sign in — ChooseChow',
    description: 'Sign in to your ChooseChow account.',
    indexable: false,
  },
  {
    path: '/register',
    title: 'Create an account — ChooseChow',
    description: 'Create a ChooseChow account to order food, or to start selling from your kitchen.',
    indexable: false,
  },
  {
    path: '/dashboard',
    title: 'Your food — ChooseChow',
    description: 'Kitchens, dishes and meal plans near you.',
    indexable: false,
  },
  { path: '/chefs', title: 'Kitchens — ChooseChow', description: 'Browse kitchens cooking near you.', indexable: false },
  { path: '/menus', title: 'Menus — ChooseChow', description: 'Explore published dish collections from local kitchens.', indexable: false },
  { path: '/dishes', title: 'Dishes — ChooseChow', description: 'Browse dishes available near you.', indexable: false },
  { path: '/market', title: 'Market — ChooseChow', description: 'Fresh produce and pantry from market vendors.', indexable: false },
  { path: '/orders', title: 'Your orders — ChooseChow', description: 'Track your ChooseChow orders.', indexable: false },
  { path: '/cart', title: 'Your cart — ChooseChow', description: 'Review your ChooseChow order before checkout.', indexable: false },
  { path: '/wallet', title: 'Wallet — ChooseChow', description: 'Your ChooseChow balance and transactions.', indexable: false },
  { path: '/profile', title: 'Your profile — ChooseChow', description: 'Your ChooseChow account details.', indexable: false },
  { path: '/subscriptions', title: 'Meal planning — ChooseChow', description: 'Plan meals for yourself or your household.', indexable: false },
  { path: '/mealplan', title: 'Meal plan — ChooseChow', description: 'Plan your week of meals on ChooseChow.', indexable: false },
  { path: '/allergies', title: 'Allergies — ChooseChow', description: 'Tell kitchens what you cannot eat.', indexable: false },
  { path: '/memberships', title: 'Meal planning — ChooseChow', description: 'Plan meals from local kitchens.', indexable: false },
  { path: '/promotions', title: 'Kitchen promotions — ChooseChow', description: 'Manage discounts for your kitchen and dishes.', indexable: false },
  { path: '/ops', title: 'Operations — ChooseChow', description: 'ChooseChow operations sign-in and overview.', indexable: false },
  { path: '/ops/promotions', title: 'Promotions — ChooseChow Operations', description: 'Manage ChooseChow campaigns and pricing.', indexable: false },
  { path: '/ops/messages', title: 'Messages — ChooseChow Operations', description: 'Review customer and chef conversations.', indexable: false },
  { path: '/menu', title: 'Your menu — ChooseChow', description: 'Curate what your kitchen is selling.', indexable: false },
  { path: '/verification', title: 'Kitchen verification — ChooseChow', description: 'Verify your kitchen to sell on ChooseChow.', indexable: false },
  { path: '/schedule', title: 'Your schedule — ChooseChow', description: 'Set the hours your kitchen cooks.', indexable: false },
  { path: '/inbox', title: 'Inbox — ChooseChow', description: 'Your ChooseChow conversations.', indexable: false },
  { path: '/help', title: 'Help — ChooseChow', description: 'Get help with a ChooseChow order.', indexable: false },
  { path: '/account/delete', title: 'Delete account — ChooseChow', description: 'Close your ChooseChow account.', indexable: false },
]

/** Normalise a pathname to the form this table is keyed on. */
export function normalizePath(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, '')
  return trimmed === '' ? '/' : trimmed
}

/**
 * The metadata for a path.
 *
 * An unrouted path returns undefined rather than falling back to the homepage's
 * entry. The homepage's identity is the homepage's alone: handing it to an
 * unknown URL is what makes every stray link on the internet a duplicate of the
 * front page.
 */
export function routeMetaFor(pathname: string): RouteMeta | undefined {
  return ROUTE_META.find((route) => route.path === normalizePath(pathname))
}

/** The absolute URL a page claims as its own. */
export function canonicalFor(path: string): string {
  const normalized = normalizePath(path)
  return normalized === '/' ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${normalized}`
}
